import { Component, OnInit, ViewChild, NgZone, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationStore } from './permission/services/authentication.store';
import { Observable } from 'rxjs/Observable';
import { MenuStore } from './menus/service/menu.store';
import { PageScrollConfig } from 'ng2-page-scroll';
import { Angulartics2GoogleAnalytics } from 'angulartics2';
import { Router, NavigationEnd } from '@angular/router';
import { FlashMessagesComponent } from './shared/flash-messages/component/flash.messages.component';
import { User } from './auth/models/User';
import { ExpirationModalComponent } from './shared/expiration-modal/expiration-modal.component';
import { ExpirationModalConfig } from './shared/expiration-modal/expiration-modal-config';
import { Constants } from './shared/constants';
import { FlashMessage, MessageLevel } from './shared/flash-messages/flash.message.record';
import { LayoutService } from './services/layout.service';
import { Subscription } from 'rxjs/Subscription';

@Component({
  selector: 'pioneer-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {

  private showLayoutSubscription: Subscription;
  showLayout = true;

  @ViewChild(FlashMessagesComponent) flashMessageComponent: FlashMessagesComponent;
  @ViewChild(ExpirationModalComponent) expirationModal: ExpirationModalComponent;
  expirationModalConfig = new ExpirationModalConfig();

  user: User;
  userIsLoggedIn: Observable<boolean>;
  title: string;
  isPropertySubscribed: boolean;

  constructor(private translate: TranslateService,
              private authenticationStore: AuthenticationStore,
              menuStore: MenuStore,
              angulartics2GoogleAnalytics: Angulartics2GoogleAnalytics,
              public ngZone: NgZone,
              private router: Router,
              private layoutService: LayoutService) {
    // Configure the defaults and setup the translation service from ng-translate.
    translate.setDefaultLang('en-US');
    translate.use('en-US');

    // need to change duration, which otherwise would be 1250 milliseconds which feels very slow
    PageScrollConfig.defaultDuration = 0;

    this.userIsLoggedIn = authenticationStore.isLoggedIn();
    menuStore.pageTitle.subscribe(pageTitle => {
      // to remove the ExpressionChangedAfterItHasBeenCheckedError
      // https://github.com/angular/angular/issues/17572
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            this.title = pageTitle;
          });
        }, 0);
      });

    });

    menuStore.isPropertySubscribed.subscribe(propertySubscribed => {
      // to remove the ExpressionChangedAfterItHasBeenCheckedError
      // https://github.com/angular/angular/issues/17572
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            this.isPropertySubscribed = propertySubscribed;
          });
        }, 0);
      });

    });

  }

  ngOnInit() {
    this.resetPage();
    this.authenticationStore.user.subscribe((user: User) => {
      this.user = user;
    });
    let authenticated = false;
    this.userIsLoggedIn.subscribe((loggedIn: boolean) => {
      authenticated = loggedIn;
    });
    // NgZone is used to run the interval because Protractor will wait forever for Observable.interval to finish
    // https://github.com/juliemr/ngconf-2016-zones/blob/master/src/app/main.ts#L88
    this.ngZone.runOutsideAngular(() => {
      setInterval(() => {
        this.ngZone.run(() => {
          let diff;
          if (this.user && this.user.expiration) {
            diff = Date.parse(this.user.expiration.toString()) - Date.parse(new Date().toString());
          } else {
            diff = 0;
          }
          const seconds = this.getSeconds(diff);
          if (seconds <= Constants.SESSION_EXPIRE_WARNING_SECONDS && authenticated) {
            if (this.expirationModal.closed) {
              this.expirationModal.open();
            }
            if (seconds <= 0 ) {
              this.expirationModal.close(false);
              this.authenticationStore.logout();
              this.expirationModal.setAlreadyExpired(true);
              this.expirationModalConfig.content = this.translate.instant('AUTHENTICATION_EXPIRATION.CONTENT_EXPIRED');
              const message = new FlashMessage(MessageLevel.ERROR, this.translate.instant('EXTERNAL_LOGIN_EXPIRED.LOG_IN_AGAIN'), true);
              this.flashMessageComponent.show(message);
            } else {
              this.expirationModal.setAlreadyExpired(false);
              this.expirationModalConfig.content = this.translate.instant('AUTHENTICATION_EXPIRATION.CONTENT_NOT_EXPIRED', {
                seconds: seconds
              });
            }
          }
        });
      }, 1000);
    });

    this.showLayoutSubscription = this.layoutService.showLayout.subscribe((showLayout: boolean) => {
      this.showLayout = showLayout;
    });
  }

  ngOnDestroy(): void {
    if (this.showLayoutSubscription) {
      this.showLayoutSubscription.unsubscribe();
    }
  }

  private getSeconds(t) {
    return Math.max(Math.floor(t / 1000), 0);
  }

  /**
   * Checks to see if navigation was successful and resets the page
   * to an expected state.
   */
  resetPage() {
    this.router.events.subscribe((routerEvent) => {
      if (!(routerEvent instanceof NavigationEnd)) {
        return;
      }
      /**
       * Forces the app to scroll to the top of page when navigation is successful,
       * solving the issue where the user would change pages and stay in the middle
       * of the page.
       * See: https://stackoverflow.com/questions/39601026/angular-2-scroll-to-top-on-route-change
       */
      window.scrollTo(0, 0);

      /**
       * Resets any flash Messages that aren't set to be persistent.
       */
      this.flashMessageComponent.clearNonPersistentMessages();
    });
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/app.component.ts